import React, {
    createContext,
    useEffect,
    useCallback,
    useContext,
    useState,
} from 'react';
import {AuthContext} from '../../context/AuthContext';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import axios from 'axios';
import Cache, {getSignature} from 'utils/cache';

const axiosClient = axios.create({});

const AxiosContext = createContext({client: axiosClient, ready: false});

const AxiosProvider = ({children}) => {
    const {setAuthToken, logOut, isLoading: authLoading} = useContext(AuthContext);
    const [ready, setReady] = useState(false);

    const onUnauthorized = () => {
        Cache.clearStore();
        logOut();
    };

    const errorHandler = error => {
        // intercept errors. If its one thrown by us (cached) rsolve, otherwise reject
        return error?.cached ? Promise.resolve(error) : Promise.reject(error);
    };

    const requestInterceptor = useCallback(
        async config => {
            const signature = getSignature(config);

            if (!window.Cypress) {
                Cache.pruneEndpoints(
                    config.method,
                    config.url,
                    config.params || null
                );
            }

            const user = firebase.auth().currentUser;
            const masquerade = sessionStorage.getItem('admin.masquerade');
            const cache = Cache.endpointStore();

            if (user) {
                const token = await user.getIdToken();
                config.headers['Authorization'] = token;
                setAuthToken(token);
            }

            if (masquerade && !config.url.match(/\/api\/\d+\/admin.*/)) {
                config.headers['X-EMAIL'] = masquerade;
            }

            if (
                config.useCache &&
                Object.keys(cache).includes(signature) &&
                !window.Cypress
            ) {
                // found a cache for this endpoint use that instead
                //eslint-disable-next-line no-throw-literal
                throw {cached: true, ...cache[signature]};
            }
            return config;
        },
        [setAuthToken]
    );

    const responseInterceptor = useCallback(async config => {
        const signature = getSignature(config.config);
        if (config.config.cache && !window.Cypress) {
            // save new get requests to cache
            Cache.updateStore(signature, config);
        }
        return config;
    }, []);

    useEffect(() => {
        if (!authLoading) {
            // Create Request Interceptor
            const interceptor = axiosClient.interceptors.request.use(
                requestInterceptor,
                errorHandler
            );
            const respInterceptor = axiosClient.interceptors.response.use(
                responseInterceptor,
                errorHandler
            );
            setReady(!!firebase.auth().currentUser || window.Cypress);

            return () => {
                axiosClient.interceptors.request.eject(interceptor);
                axiosClient.interceptors.response.eject(respInterceptor);
                setReady(false);
            };
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authLoading, requestInterceptor, responseInterceptor]);

    useEffect(() => {
        if (!authLoading) {
            const interceptor = axiosClient.interceptors.response.use(
                response => {
                    if (response.status === 202) {
                        // User doesn't exist, must onboard"
                    }

                    return response;
                },
                error => {
                    if (
                        error?.response?.status === 401 &&
                        !firebase.auth().currentUser
                    ) {
                        onUnauthorized();
                    }
                    console.error(error.message);
                    return Promise.reject(error);
                }
            );

            return () => {
                axiosClient.interceptors.response.eject(interceptor);
            };
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authLoading]);

    return (
        <AxiosContext.Provider
            displayName='Axios'
            value={{
                client: ready ? axiosClient : null,
                ready,
            }}
        >
            {children}
        </AxiosContext.Provider>
    );
};

export default AxiosProvider;
export {AxiosContext};
